/**
 * THE FILTER PICKER (0.1.456) — one surface for adding a filter, at both widths: a list of
 * DIMENSIONS in plain words ("Source — who the parcel came from"), and behind each one its
 * VALUES as toggles. Every tap applies at once (maintainer, 3 Sep 2026: "everything should be
 * applied the moment it is picked") — no draft, no "Show N", no Sort section: sorting and
 * grouping belong to the Display bar / sheet, and the maintainer's report said exactly that
 * ("there's a sort and group option in this filter sheet, which isn't a filter").
 *
 * Entity-agnostic like the Display panel: the screen supplies `FilterDimension`s built from
 * its own filter contract (`box-filters.ts`, `item-filters.ts`), and the picker never learns
 * what a box is. Two homes, one markup:
 *  - the DESK: an anchored popover under the bar's "+ Filter" (the `display-menu` idiom,
 *    outside-press / Escape through `useDismiss`);
 *  - the PHONE: a `Sheet` opened from the Display sheet's "+ Add filter", elevated so it
 *    rides above that sheet's scrim.
 *
 * Why not the OptionSheet for the values: it is z 11 and cannot rise above the phone's
 * elevated Display sheet (z 20), and its multi mode commits on Done — the opposite of the
 * ruling above. The value step here keeps the two things the specs and the atelier rely on
 * from it: a "Search <label>" box, and "Use “<typed>”" for a free-entry dimension.
 *
 * Until 0.1.456 the desk's "+ Filter" opened the PHONE filter sheet — a draft with "Show N",
 * a Sort section that fought the Display bar, and, from inside the panel, BEHIND it (it read
 * the phone sheet's open state for its elevation). The old sheets stay for the scoped flat
 * lists (a box's items, a reservation's), which have no Display control.
 */
import { useEffect, useRef, useState } from 'react';
import { Sheet } from '../components/sheet.js';
import { useDismiss } from './use-dismiss.js';

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterDimension {
  id: string;
  label: string;
  /** One line, muted, under the label — what picking here does. */
  hint: string;
  options: FilterOption[];
  /** The lit values (by `value`). */
  selected: string[];
  /** Several values may be lit (OR-combined) — a set dimension; false = one value at a time. */
  multi: boolean;
  /** Offer "Use “<typed>”" when nothing matches — values free-typed at the rack are real. */
  allowFree?: boolean;
  /** Flip one value. The screen applies it at once. */
  toggle: (value: string) => void;
}

export function FilterPicker({
  dimensions,
  onClose,
  variant,
  /** Which dimension opens first (the panel's per-dimension doors, later); absent = the list. */
  initial = null,
}: {
  dimensions: FilterDimension[];
  onClose: () => void;
  variant: 'desk' | 'phone';
  initial?: string | null;
}): JSX.Element {
  const [step, setStep] = useState<string | null>(initial);
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const dim = step === null ? undefined : dimensions.find((d) => d.id === step);

  useEffect(() => {
    setQuery('');
    if (step !== null) searchRef.current?.focus();
  }, [step]);

  const body = (
    <div className={`filter-picker filter-picker-${variant}`}>
      {dim === undefined ? (
        <>
          <p className="display-panel-head filter-picker-head">
            Add a filter
            {variant === 'phone' && (
              <button type="button" className="linklike display-close" onClick={onClose}>
                Close
              </button>
            )}
          </p>
          <ul className="filter-picker-list">
            {dimensions.map((d) => (
              <li key={d.id}>
                <button type="button" className="filter-picker-row" onClick={() => setStep(d.id)}>
                  <span className="filter-picker-row-main">
                    <span className="filter-picker-label">{d.label}</span>
                    <span className="filter-picker-hint">{d.hint}</span>
                  </span>
                  {d.selected.length > 0 && <span className="display-bar-count">{d.selected.length}</span>}
                  <span className="filter-row-chevron" aria-hidden="true">
                    ›
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <>
          <p className="display-panel-head filter-picker-head">
            <button type="button" className="filter-picker-back" onClick={() => setStep(null)} aria-label="Back to the filters">
              ‹
            </button>
            {dim.label}
            {variant === 'phone' && (
              <button type="button" className="linklike display-close" onClick={onClose}>
                Close
              </button>
            )}
          </p>
          <input
            ref={searchRef}
            type="search"
            className="filter-picker-search"
            placeholder="Type to filter…"
            aria-label={`Search ${dim.label}`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <FilterValues dim={dim} query={query} onPicked={() => (dim.multi ? undefined : setStep(null))} />
        </>
      )}
    </div>
  );

  if (variant === 'phone') {
    return (
      <Sheet label="Filter" onClose={onClose} className="filter-picker-sheet" elevated>
        {body}
      </Sheet>
    );
  }
  return <DeskPopover onClose={onClose}>{body}</DeskPopover>;
}

function DeskPopover({ onClose, children }: { onClose: () => void; children: JSX.Element }): JSX.Element {
  const ref = useDismiss(true, onClose);
  return (
    <div ref={ref} className="display-menu filter-picker-pop" role="dialog" aria-label="Filter">
      {children}
    </div>
  );
}

function FilterValues({ dim, query, onPicked }: { dim: FilterDimension; query: string; onPicked: () => void }): JSX.Element {
  const q = query.trim().toLowerCase();
  const shown = q === '' ? dim.options : dim.options.filter((o) => o.label.toLowerCase().includes(q));
  const exact = dim.options.some((o) => o.label.toLowerCase() === q);
  const lit = new Set(dim.selected);
  return (
    <ul className="filter-picker-list filter-picker-values" role="group" aria-label={dim.label}>
      {shown.map((o) => (
        <li key={o.value}>
          <button
            type="button"
            className={lit.has(o.value) ? 'filter-picker-row filter-picker-row-on' : 'filter-picker-row'}
            aria-pressed={lit.has(o.value)}
            onClick={() => {
              dim.toggle(o.value);
              onPicked();
            }}
          >
            <span className="filter-picker-label">{o.label}</span>
            {lit.has(o.value) && (
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path d="M2.5 7.5 L5.5 10.5 L11.5 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </button>
        </li>
      ))}
      {dim.allowFree === true && q !== '' && !exact && (
        <li>
          <button
            type="button"
            className="filter-picker-row"
            onClick={() => {
              dim.toggle(query.trim());
              onPicked();
            }}
          >
            <span className="filter-picker-label">Use “{query.trim()}”</span>
          </button>
        </li>
      )}
      {shown.length === 0 && (dim.allowFree !== true || q === '') && (
        <li className="filter-picker-empty">Nothing matches.</li>
      )}
    </ul>
  );
}
